import { DEFAULT_ORGANIZATION_SETTINGS } from '@paragon/contract-library';
import { designService, sealingService } from '@paragon/data-connections';
import { loadProjectsAndComponentDesignSummaryLayoutModels } from '@paragon/layout-models';
import { loadOrganizationMembers } from '@paragon/login-tools';
import { loadCurrentOrganizationMaterialSet } from '@paragon/material-lists';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { sortBy } from '@paragon/utilities';

import { annotationTemplatesLoaded } from '../slices/annotationTemplatesSlice';
import {
  applicationFullyLoaded,
  organizationCapabilitiesSet,
  organizationSettingsSet,
} from '../slices/applicationSlice';
import { AsyncThunkActionVoid } from './thunkActions';

export default (): AsyncThunkActionVoid => async (dispatch, getState) => {
  dispatch(loadOrganizationMembers());
  dispatch(loadCurrentOrganizationMaterialSet());

  const spinnerId = dispatch(addSpinnerMessage('Loading projects...'));

  let errorReported = false;

  const reportProjectLoadError = (err: unknown) => {
    console.error(err);

    if (!errorReported) {
      errorReported = true;
      dispatch(enqueueError('Error loading projects.'));
    }
  };

  try {
    // Sort the projects so that when we incrementally load the projects into state, the newest ones load first and
    // don't get displaced in the grid.
    const projects = sortBy(await designService.projects.fetchAll(), (project) => new Date(project.createdAt), 'desc');

    const existingLayoutModels = getState().layoutModels;
    if (projects.some((project) => project.guid in existingLayoutModels)) {
      throw new Error(
        'An attempt was made to re-load a project.  This is prohibited because it can lead to duplicated insertions.',
      );
    }

    const projectLoader = dispatch(
      loadProjectsAndComponentDesignSummaryLayoutModels(projects, { firstBatchSize: 100 }),
    );

    projectLoader.whenAllLoaded
      .then(() => void dispatch(applicationFullyLoaded()))
      .catch((err) => {
        console.error(err);
        dispatch(enqueueError('Error loading background data. Please reload the page.'));
      });

    try {
      const annotationTemplates = await sealingService.annotationTemplates.fetchAllForUserWithoutContents();
      dispatch(annotationTemplatesLoaded(annotationTemplates));
    } catch (err) {
      console.error(err);
      dispatch(enqueueWarning('Error loading annotation templates. Downloading some types of files may not work.'));
    }

    const organizationSettings = await designService.settings.getOrganizationSettings();
    dispatch(organizationSettingsSet(organizationSettings ?? { ...DEFAULT_ORGANIZATION_SETTINGS, organization: '' }));

    try {
      dispatch(organizationCapabilitiesSet(await designService.settings.getOrganizationCapabilities()));
    } catch (err) {
      console.error(err);
      dispatch(enqueueWarning('Error loading organization capabilities. Some features may not work.'));
    }

    await projectLoader.whenFirstBatchLoaded;
  } catch (err) {
    reportProjectLoadError(err);
    return;
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};
